/**
 * Brand Mark
 *
 * The canonical Polygraf app icon — the same artwork as the favicon, the
 * QR-code centre logo and the mobile app icon. Replaces the legacy
 * gradient-box + Sparkles placeholder chips.
 */

import { useTranslation } from 'react-i18next'
// Imported (not /public) so Vite fingerprints the URL: swapping the artwork
// changes the filename and every client — service worker included — picks the
// new brand up immediately instead of serving a stale cached /polygraf-icon.svg.
import polygrafIcon from '@/assets/polygraf-icon.svg'

const SIZE_CLASSES = {
	sm: 'w-9 h-9',
	lg: 'w-12 h-12',
} as const

interface BrandMarkProps {
	size?: keyof typeof SIZE_CLASSES
}

export function BrandMark({ size = 'sm' }: BrandMarkProps) {
	// Decorative: the adjacent wordmark / app-name text carries the accessible name.
	return (
		<img
			src={polygrafIcon}
			alt=""
			draggable={false}
			className={`${SIZE_CLASSES[size]} rounded-xl shadow-soft select-none`}
		/>
	)
}

// Brand wordmark, kept two-tone: first word in base colour, the rest in coral
// (coral-700 passes WCAG AA contrast on light backgrounds, see design-tokens F-001).
export function BrandWordmark() {
	const { t } = useTranslation()
	const [first, ...rest] = t('common.appName').split(' ')
	return (
		<div>
			<span className="font-display text-xl text-base-content">{first}</span>
			{rest.length > 0 && (
				<span className="font-display text-xl text-coral-700 ml-1">{rest.join(' ')}</span>
			)}
		</div>
	)
}
